<template>
  <div>
    <h5 class="mb-4"><strong>Who are you recognising?</strong></h5>

    <div class="section-label">Add people</div>

    <div class="search-container">
      <div class="search-input-wrapper">
        <font-awesome-icon :icon="['fal', 'magnifying-glass']" class="search-icon" />
        <b-form-input
          v-model="searchText"
          type="search"
          placeholder="Find members"
          class="search-input-with-icon"
          @input="onSearchInput"
        />
      </div>

      <div v-if="isSearching" class="search-dropdown">
        <div class="text-center py-3">
          <b-spinner small variant="primary" />
        </div>
      </div>

      <div v-else-if="filteredSearchResults.length > 0" class="search-dropdown">
        <div
          v-for="employee in filteredSearchResults"
          :key="employee.employee_id"
          class="search-dropdown__item"
          role="button"
          @mousedown.prevent="addEmployee(employee)"
        >
          <div>
            <div class="search-dropdown__name">{{ employee.full_name }}</div>
            <div v-if="employee.division_name" class="search-dropdown__subtitle">{{ employee.division_name }}</div>
          </div>
        </div>
      </div>

      <div v-else-if="searchText.length >= 3 && !isSearching" class="search-dropdown">
        <div class="text-muted py-3 px-3">No employees found</div>
      </div>
    </div>

    <div v-if="selectedEmployees.length > 0" class="selected-tags mt-3">
      <span v-for="emp in selectedEmployees" :key="emp.employee_id" class="selected-tag">
        {{ emp.full_name }}
        <font-awesome-icon :icon="['fal', 'xmark']" class="remove-tag" role="button" @click="$emit('remove', emp)" />
      </span>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, ref } from 'vue';
import ApiClient from '@/ApiClient';
import State from '@/state/State';

const props = defineProps({
  program: {
    type: Object,
    default: null,
  },
  selectedEmployees: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['add', 'remove']);

const searchText = ref('');
const searchResults = ref([]);
const isSearching = ref(false);

let debounceTimeout;
let searchRequestId = 0;

// An admin cannot award themselves — the API rejects the whole batch — so keep
// them out of the picker rather than surfacing that as a submit-time failure.
const currentEmployeeId = computed(() => State.state.claims?.eid);

const filteredSearchResults = computed(() => {
  const excludedIds = new Set(props.selectedEmployees.map(e => e.employee_id));
  if (currentEmployeeId.value) {
    excludedIds.add(currentEmployeeId.value);
  }
  return searchResults.value.filter(e => !excludedIds.has(e.employee_id));
});

function clearSearch() {
  searchText.value = '';
  searchResults.value = [];
  isSearching.value = false;
  searchRequestId++;
  if (debounceTimeout) {
    clearTimeout(debounceTimeout);
  }
}

function onSearchInput() {
  if (debounceTimeout) {
    clearTimeout(debounceTimeout);
  }

  if (searchText.value.length < 3) {
    searchResults.value = [];
    isSearching.value = false;
    searchRequestId++;
    return;
  }

  debounceTimeout = setTimeout(async () => {
    const requestId = ++searchRequestId;
    isSearching.value = true;
    try {
      const data = await ApiClient.searchRecognitionEmployees({
        searchText: searchText.value,
        divisionIds: props.program?.enabled_divisions || [],
        limit: 10,
      });
      if (requestId === searchRequestId) {
        searchResults.value = data.data;
      }
    } catch {
      if (requestId === searchRequestId) {
        searchResults.value = [];
      }
    } finally {
      if (requestId === searchRequestId) {
        isSearching.value = false;
      }
    }
  }, 250);
}

function addEmployee(employee) {
  emit('add', employee);
  clearSearch();
}

onBeforeUnmount(clearSearch);

defineExpose({ clearSearch });
</script>

<style scoped lang="scss">
@import '@/pages/recognition/styles/give-recognition';

.search-container {
  position: relative;
}

.search-input-wrapper {
  position: relative;
}

.search-dropdown {
  position: absolute;
  z-index: 1000;
  width: 100%;
  background: var(--palette-color-base-white);
  border: 1px solid var(--palette-color-default-lighten-90);
  border-radius: 0.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  max-height: 15rem;
  overflow-y: auto;
  margin-top: 0.25rem;

  &__item {
    padding: 0.625rem 0.75rem;
    cursor: pointer;
    transition: background-color 0.1s ease;

    &:hover {
      background-color: var(--palette-color-default-lighten-95);
    }
  }

  &__name {
    font-weight: 500;
    font-size: 0.875rem;
  }

  &__subtitle {
    font-size: 0.8rem;
    color: var(--palette-color-default-lighten-30);
  }
}

.search-icon {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--palette-color-default-lighten-30);
  z-index: 1;
  pointer-events: none;
}

.search-input-with-icon {
  padding-left: 2.25rem;
}

.selected-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
}

.selected-tag {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  background-color: var(--palette-color-default-lighten-95);
  color: var(--palette-color-default);
  border: 1px solid var(--palette-color-default-lighten-90);
  border-radius: 1rem;
}

.remove-tag {
  cursor: pointer;
  opacity: 0.6;

  &:hover {
    opacity: 1;
  }
}
</style>
